/**
 * Service Frontend - Gestion des restrictions et bans (Admin)
 */
import api from './api';

const BASE_URL = '/admin';

const restrictionAdminService = {
  // =====================================================
  // RESTRICTIONS
  // =====================================================
  
  /**
   * Liste toutes les restrictions avec filtres
   * @param {Object} params - { status, type, userId, page, limit }
   */
  async listRestrictions(params = {}) {
    const response = await api.get(`${BASE_URL}/restrictions`, { params });
    return response.data;
  },
  
  /**
   * Récupère les détails d'une restriction
   * @param {string} restrictionId
   */
  async getRestriction(restrictionId) {
    const response = await api.get(`${BASE_URL}/restrictions/${restrictionId}`);
    return response.data;
  },
  
  /**
   * Crée une nouvelle restriction
   * @param {Object} data - { userId, restrictionType, scope, reason, expiresAt, metadata }
   */
  async createRestriction(data) {
    const response = await api.post(`${BASE_URL}/restrictions`, data);
    return response.data;
  },
  
  /**
   * Lève une restriction
   * @param {string} restrictionId
   * @param {string} reason
   */
  async liftRestriction(restrictionId, reason) {
    const response = await api.post(`${BASE_URL}/restrictions/${restrictionId}/lift`, { reason });
    return response.data;
  },
  
  // =====================================================
  // BANS
  // =====================================================
  
  /**
   * Liste tous les utilisateurs bannis
   * @param {Object} params - { banType, page, limit }
   */
  async listBans(params = {}) {
    const response = await api.get(`${BASE_URL}/bans`, { params });
    return response.data;
  },
  
  /**
   * Crée un ban temporaire
   * @param {Object} data - { userId, durationDays, reason }
   */
  async createTemporaryBan(data) {
    const response = await api.post(`${BASE_URL}/bans/temporary`, data);
    return response.data;
  },
  
  /**
   * Crée un ban permanent
   * @param {Object} data - { userId, reason }
   */
  async createPermanentBan(data) {
    const response = await api.post(`${BASE_URL}/bans/permanent`, data);
    return response.data;
  },
  
  /**
   * Débannit un utilisateur
   * @param {string} userId
   * @param {string} reason
   */
  async unbanUser(userId, reason) {
    const response = await api.post(`${BASE_URL}/bans/${userId}/unban`, { reason });
    return response.data;
  },
  
  // =====================================================
  // STRIKES
  // =====================================================
  
  /**
   * Liste tous les strikes avec filtres
   * @param {Object} params - { userId, infractionType, isActive, page, limit }
   */
  async listStrikes(params = {}) {
    const response = await api.get(`${BASE_URL}/strikes`, { params });
    return response.data;
  },
  
  /**
   * Récupère les détails d'un strike
   * @param {string} strikeId
   */
  async getStrike(strikeId) {
    const response = await api.get(`${BASE_URL}/strikes/${strikeId}`);
    return response.data;
  },
  
  /**
   * Ajoute un strike
   * @param {Object} data - { userId, infractionType, reason, customPoints, penaltyId, disputeId, reservationId, metadata }
   */
  async addStrike(data) {
    const response = await api.post(`${BASE_URL}/strikes`, data);
    return response.data;
  },
  
  /**
   * Annule un strike
   * @param {string} strikeId
   * @param {string} reason
   */
  async voidStrike(strikeId, reason) {
    const response = await api.post(`${BASE_URL}/strikes/${strikeId}/void`, { reason });
    return response.data;
  },
  
  // =====================================================
  // USER SUMMARY
  // =====================================================
  
  /**
   * Récupère le résumé des restrictions d'un utilisateur
   * @param {string} userId
   */
  async getUserRestrictionSummary(userId) {
    const response = await api.get(`${BASE_URL}/users/${userId}/restriction-summary`);
    return response.data;
  },
  
  /**
   * Récupère l'historique des restrictions d'un utilisateur
   * @param {string} userId
   * @param {Object} params - { page, limit }
   */
  async getUserRestrictionHistory(userId, params = {}) {
    const response = await api.get(`${BASE_URL}/users/${userId}/restriction-history`, { params });
    return response.data;
  },
  
  /**
   * Récupère l'historique des strikes d'un utilisateur
   * @param {string} userId
   * @param {Object} params - { page, limit }
   */
  async getUserStrikeHistory(userId, params = {}) {
    const response = await api.get(`${BASE_URL}/users/${userId}/strike-history`, { params });
    return response.data;
  },

  /**
   * Récupère les informations basiques d'un utilisateur (nom, email, langue)
   * @param {string} userId
   */
  async getUserBasicInfo(userId) {
    const response = await api.get(`${BASE_URL}/users/${userId}/basic-info`);
    return response.data;
  },
  
  // =====================================================
  // STATISTIQUES & AUDIT
  // =====================================================
  
  /**
   * Récupère les statistiques globales
   */
  async getStats() {
    const response = await api.get(`${BASE_URL}/stats/overview`);
    return response.data;
  },
  
  /**
   * Récupère les événements d'audit
   * @param {Object} params - { userId, eventType, startDate, endDate, page, limit }
   */
  async getEvents(params = {}) {
    const response = await api.get(`${BASE_URL}/events`, { params });
    return response.data;
  },
  
  // =====================================================
  // RECHERCHE UTILISATEURS
  // =====================================================
  
  /**
   * Recherche d'utilisateurs par nom ou email
   * @param {string} query - Terme de recherche (min 2 caractères)
   * @param {number} limit - Nombre maximum de résultats (défaut: 10)
   */
  async searchUsers(query, limit = 10) {
    const response = await api.get(`${BASE_URL}/users/search`, { 
      params: { query, limit } 
    });
    return response.data;
  },
  
  // =====================================================
  // CONSTANTES
  // =====================================================
  
  RESTRICTION_TYPES: [
    { value: 'booking_block', label: 'Blocage réservations' },
    { value: 'listing_unpublish', label: 'Dépublication annonces' },
    { value: 'message_block', label: 'Blocage messages' },
    { value: 'payout_hold', label: 'Gel des versements' },
    { value: 'payin_block', label: 'Blocage paiements' },
    { value: 'kyc_hold', label: 'Gel KYC' },
    { value: 'ban_temporary', label: 'Ban temporaire' },
    { value: 'ban_permanent', label: 'Ban permanent' }
  ],
  
  RESTRICTION_SCOPES: [
    { value: 'user', label: 'Utilisateur' },
    { value: 'listing', label: 'Annonce (listing)' },
    { value: 'reservation', label: 'Réservation' }
  ],
  
  INFRACTION_TYPES: [
    { value: 'no_show', label: 'No-show', points: 3 },
    { value: 'late_return', label: 'Retour en retard', points: 1 },
    { value: 'damage_unreported', label: 'Dégât non signalé', points: 2 },
    { value: 'policy_violation', label: 'Violation CGU', points: 2 },
    { value: 'fraud_attempt', label: 'Tentative de fraude', points: 5 },
    { value: 'harassment', label: 'Harcèlement', points: 4 },
    { value: 'false_claim', label: 'Réclamation frauduleuse', points: 3 },
    { value: 'content_violation', label: 'Contenu inapproprié', points: 1 },
    { value: 'payment_fraud', label: 'Fraude au paiement', points: 5 },
    { value: 'identity_fraud', label: 'Usurpation identité', points: 5 },
    { value: 'repeated_cancellation', label: 'Annulations répétées', points: 2 },
    { value: 'admin_decision', label: 'Décision admin', points: 3 }
  ],
  
  BAN_STATUS_COLORS: {
    none: 'success',
    temporary: 'warning',
    permanent: 'error'
  },
  
  RESTRICTION_STATUS_COLORS: {
    active: 'error',
    expired: 'default',
    lifted: 'success'
  }
};

export default restrictionAdminService;
